import { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { CheckCircle2, Star, Calendar, Phone, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function Coaching() {
  const [plans, setPlans] = useState([]);
  const [activePlan, setActivePlan] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", preferred_time: "", notes: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => { api.get("/coaching/plans").then((r) => setPlans(r.data)); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!activePlan) return;
    setLoading(true);
    try {
      const { data } = await api.post("/coaching/book", {
        plan_id: activePlan.id,
        ...form,
        origin_url: window.location.origin,
      });
      if (data.checkout_url) {
        // Send user to Stripe to prepay their session
        window.location.href = data.checkout_url;
        return;
      }
      setDone(data);
      setForm({ name: "", email: "", phone: "", preferred_time: "", notes: "" });
      toast.success("Booking received — Kristy will reach out within 24 hours.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not submit booking.");
    } finally { setLoading(false); }
  };

  return (
    <div className="bg-white min-h-screen">
      <Navbar />
      <section className="py-16 max-w-7xl mx-auto px-6 lg:px-10">
        <div className="uppercase tracking-[0.28em] text-xs text-brand-pink mb-3">Host Hustle Coaching</div>
        <h1 className="font-display text-5xl md:text-6xl max-w-3xl leading-tight">Get in a room with <span className="italic text-brand-pink">Kristy</span>.</h1>
        <p className="mt-6 text-neutral-600 max-w-2xl text-lg">Two ways to work with Kristy directly — a focused 60-minute strategy session, or a full 12-week mentorship that rebuilds your business.</p>

        <div className="grid lg:grid-cols-2 gap-6 mt-14">
          {plans.map((p) => (
            <div
              key={p.id}
              data-testid={`coaching-plan-${p.id}`}
              className={`rounded-3xl border p-8 lg:p-10 relative overflow-hidden transition ${p.id === "co-host-mentorship" ? "bg-neutral-950 text-white border-neutral-900" : "bg-white border-neutral-100 card-soft"}`}
            >
              {p.id === "co-host-mentorship" && (
                <div className="absolute top-6 right-6 px-3 py-1 rounded-full bg-brand-pink text-white text-[10px] uppercase tracking-widest">Most transformative</div>
              )}
              <div className={`text-xs uppercase tracking-[0.28em] mb-3 ${p.id === "co-host-mentorship" ? "text-brand-pink" : "text-brand-pink"}`}>{p.duration}</div>
              <h3 className="font-display text-3xl mb-2">{p.name}</h3>
              <p className={`${p.id === "co-host-mentorship" ? "text-white/70" : "text-neutral-600"} mb-8`}>{p.tagline}</p>
              <div className="font-display text-5xl mb-2">${p.price.toLocaleString()}</div>
              <div className={`text-xs ${p.id === "co-host-mentorship" ? "text-white/50" : "text-neutral-500"} mb-8`}>one-time · payment plans available</div>
              <ul className="space-y-3 mb-8">
                {p.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm">
                    <CheckCircle2 className={`w-4 h-4 mt-0.5 flex-shrink-0 ${p.id === "co-host-mentorship" ? "text-brand-pink" : "text-brand-pink"}`} />
                    <span className={p.id === "co-host-mentorship" ? "text-white/85" : "text-neutral-700"}>{f}</span>
                  </li>
                ))}
              </ul>
              <Button
                onClick={() => setActivePlan(p)}
                data-testid={`coaching-book-${p.id}`}
                size="lg"
                className={`w-full rounded-full ${p.id === "co-host-mentorship" ? "bg-brand-pink hover:bg-brand-pink/90 text-white btn-pink-glow" : "bg-neutral-900 hover:bg-neutral-800 text-white"}`}
              >
                {p.id === "co-host-mentorship" ? "Apply for Mentorship" : "Book Your Session"}
              </Button>
            </div>
          ))}
        </div>

        <div className="mt-16 grid md:grid-cols-3 gap-6">
          {[
            { icon: Phone, title: "Discovery first", body: "Every booking starts with a 15-min discovery call so we make sure we're a fit." },
            { icon: Calendar, title: "Book within 24 hrs", body: "Kristy or her team responds within one business day with time slots." },
            { icon: Sparkles, title: "Action, not fluff", body: "Every session ends with a written action plan. You leave knowing exactly what to do." },
          ].map((c, i) => (
            <div key={i} className="rounded-3xl bg-neutral-50 p-6">
              <div className="w-10 h-10 rounded-2xl bg-brand-pink/15 flex items-center justify-center mb-4">
                <c.icon className="w-5 h-5 text-brand-pink" />
              </div>
              <div className="font-display text-lg mb-2">{c.title}</div>
              <p className="text-sm text-neutral-600">{c.body}</p>
            </div>
          ))}
        </div>
      </section>

      {activePlan && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur overflow-y-auto py-10 px-4" onClick={() => !loading && !done && setActivePlan(null)}>
          <div onClick={(e) => e.stopPropagation()} className="max-w-lg mx-auto rounded-3xl bg-white p-8" data-testid="coaching-booking-modal">
            {done ? (
              <div className="text-center py-6">
                <div className="w-16 h-16 rounded-full bg-brand-blush mx-auto flex items-center justify-center mb-4">
                  <CheckCircle2 className="w-8 h-8 text-brand-pink" />
                </div>
                <div className="font-display text-3xl mb-2">You're on the list.</div>
                <p className="text-neutral-600 mb-6">Kristy will reach out within one business day to confirm your time for <strong>{done.plan.name}</strong>.</p>
                <Button onClick={() => { setDone(null); setActivePlan(null); }} className="rounded-full bg-brand-pink text-white">Great, thanks</Button>
              </div>
            ) : (
              <form onSubmit={submit}>
                <div className="uppercase tracking-[0.28em] text-xs text-brand-pink mb-2">{activePlan.name}</div>
                <h2 className="font-display text-2xl mb-2">Reserve your session</h2>
                <p className="text-sm text-neutral-500 mb-6">
                  You'll be sent to secure Stripe checkout to prepay <b className="text-neutral-800">${activePlan.price.toLocaleString()}</b>.
                  Once paid, Kristy reaches out within one business day to lock your time.
                </p>
                <div className="space-y-4">
                  <div>
                    <Label className="mb-2 block text-xs uppercase tracking-widest text-neutral-500">Name</Label>
                    <Input data-testid="coaching-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-full h-11 border-neutral-200" />
                  </div>
                  <div>
                    <Label className="mb-2 block text-xs uppercase tracking-widest text-neutral-500">Email</Label>
                    <Input data-testid="coaching-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-full h-11 border-neutral-200" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="mb-2 block text-xs uppercase tracking-widest text-neutral-500">Phone (optional)</Label>
                      <Input data-testid="coaching-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-full h-11 border-neutral-200" />
                    </div>
                    <div>
                      <Label className="mb-2 block text-xs uppercase tracking-widest text-neutral-500">Preferred time</Label>
                      <Input data-testid="coaching-time" placeholder="e.g. weekday mornings" value={form.preferred_time} onChange={(e) => setForm({ ...form, preferred_time: e.target.value })} className="rounded-full h-11 border-neutral-200" />
                    </div>
                  </div>
                  <div>
                    <Label className="mb-2 block text-xs uppercase tracking-widest text-neutral-500">What do you want to work on?</Label>
                    <Textarea data-testid="coaching-notes" rows={4} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-2xl border-neutral-200" />
                  </div>
                </div>
                <div className="flex justify-end gap-3 mt-8">
                  <Button type="button" variant="ghost" onClick={() => setActivePlan(null)}>Cancel</Button>
                  <Button type="submit" disabled={loading} data-testid="coaching-submit" className="rounded-full bg-brand-pink hover:bg-brand-pink/90 text-white">
                    {loading ? "Redirecting…" : `Continue to checkout · $${activePlan.price.toLocaleString()}`}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
      <Footer />
    </div>
  );
}
